import type { FloorDef } from '../types';
import { room, door, stairs } from '../dsl';

/**
 * 지하 — 세 개의 긴 통로가 층 전체를 잇고, 방들은 그 통로에 매달려 있다.
 *  - btunnel3: 서쪽 세로 통로(납골당·헌 가구 창고·실험실·와인 저장고, 남쪽 끝은 배수로)
 *  - btunnel1: 가운데 가로 통로(북쪽: 와인 저장고·보일러실·세탁실 / 남쪽: 식료품 창고·우물 방·창고·독방 구역)
 *  - btunnel2: 동쪽 세로 통로(북쪽 끝 석탄 창고 ↔ 남쪽 끝 하인 계단실, 중간에 얼음 창고)
 * Grid 128 x 80.
 */
export const BASEMENT: FloorDef = {
  id: 'basement',
  w: 128,
  h: 80,
  rooms: [
    // ---- 통로 ----
    room('btunnel3', '서쪽 지하 통로', 30, 6, 4, 57, 'tunnel', 'dirt', 'brick'),
    room('btunnel1', '지하 통로', 35, 38, 70, 4, 'tunnel', 'stone', 'brick'),
    room('btunnel2', '동쪽 지하 통로', 106, 4, 4, 72, 'tunnel', 'dirt', 'stone'),
    room('sewer', '배수로', 35, 60, 50, 3, 'tunnel', 'stone', 'stone', {
      note: '막다른 배수로. 어딘가에서 물 떨어지는 소리가 난다.',
    }),

    // ---- 계단 ----
    room('bstair', '지하 계단실', 111, 63, 12, 13, 'stairhall', 'stone', 'brick', {
      props: [{ kind: 'crateStack', x: 121, y: 63 }, { kind: 'barrel', x: 111, y: 75 }],
    }),
    room('coalstore', '석탄 창고', 111, 5, 12, 12, 'coal', 'dirt', 'brick', {
      props: [{ kind: 'crateStack', x: 121, y: 5 }, { kind: 'barrel', x: 111, y: 16 }],
    }),

    // ---- 서쪽 ----
    room('crypt', '납골당', 6, 10, 23, 21, 'chapel', 'stone', 'stone', {
      note: '차가운 공기. 벽마다 이름이 새겨진 관들이 놓여 있다.',
    }),
    room('bstore2', '헌 가구 창고', 6, 32, 23, 17, 'storage', 'dirt', 'brick'),
    room('lab', '오래된 실험실', 35, 6, 18, 11, 'study', 'stone', 'brick', {
      props: [{ kind: 'crateStack', x: 51, y: 15 }, { kind: 'barrel', x: 35, y: 16 }],
    }),

    // ---- 가운데 통로 북쪽 ----
    room('cellar', '와인 저장고', 35, 18, 24, 19, 'cellar', 'brick', 'brick'),
    room('boilerroom', '보일러실', 60, 24, 22, 13, 'boiler', 'stone', 'brick', {
      decor: [
        { kind: 'fusebox', x: 64, y: 23, id: 'fusebox' },
        { kind: 'poster', x: 78, y: 23 },
      ],
    }),
    room('laundry', '세탁실', 83, 24, 22, 13, 'laundry', 'stone', 'brick'),

    // ---- 동쪽 통로 ----
    room('icehouse', '얼음 창고', 90, 6, 15, 13, 'ice', 'stone', 'stone', {
      props: [{ kind: 'crateStack', x: 103, y: 17 }, { kind: 'barrel', x: 90, y: 18 }],
    }),

    // ---- 가운데 통로 남쪽 ----
    room('bpantry', '지하 식료품 창고', 35, 43, 16, 13, 'pantry', 'brick', 'brick'),
    room('wellroom', '우물 방', 52, 43, 15, 13, 'well', 'stone', 'stone', {
      props: [{ kind: 'barrel', x: 52, y: 55 }],
    }),
    room('bstore1', '지하 창고', 68, 43, 19, 13, 'storage', 'brick', 'brick'),

    // ---- 독방 구역 ----
    room('bcellhall', '독방 복도', 88, 43, 4, 24, 'passage', 'stone', 'stone', {
      props: [{ kind: 'cagedLamp', x: 88, y: 54 }],
    }),
    room('bcell', '지하 독방', 93, 45, 12, 9, 'cell', 'dirt', 'stone', { prison: true }),
    room('bcell2', '빈 독방', 93, 56, 12, 9, 'cell', 'dirt', 'stone'),
  ],
  doors: [
    // 통로끼리
    door('btunnel3', 'btunnel1', 'arch'),
    door('btunnel1', 'btunnel2', 'arch'),
    door('btunnel3', 'sewer', 'open'),

    // 서쪽 통로
    door('btunnel3', 'crypt', 'locked', { id: 'crypt-door', key: 'key_cellar', label: '납골당 문' }),
    door('btunnel3', 'bstore2', 'door', { at: 0.4 }),
    door('btunnel3', 'lab', 'door'),
    door('btunnel3', 'cellar', 'door', { at: 0.4 }),

    // 가운데 통로
    door('btunnel1', 'cellar', 'double', { at: 0.5 }),
    door('btunnel1', 'boilerroom', 'door', { at: 0.5 }),
    door('btunnel1', 'laundry', 'door', { at: 0.5 }),
    door('boilerroom', 'laundry', 'door', { at: 0.6 }),
    door('btunnel1', 'bpantry', 'door', { at: 0.5 }),
    door('btunnel1', 'wellroom', 'door', { at: 0.5 }),
    door('btunnel1', 'bstore1', 'door', { at: 0.5 }),

    // 동쪽 통로
    door('btunnel2', 'coalstore', 'door', { at: 0.6 }),
    door('btunnel2', 'icehouse', 'door', { at: 0.5 }),
    door('btunnel2', 'bstair', 'door', { at: 0.5 }),

    // 독방 구역
    door('btunnel1', 'bcellhall', 'gate', { id: 'cell-gate', trigger: 'power', label: '전기 철문' }),
    door('bcellhall', 'bcell', 'bolt', { id: 'bcell-bolt', label: '독방 문' }),
    door('bcellhall', 'bcell2', 'cell'),
  ],
  stairs: [
    stairs('backb', 'bstair', 113, 70, 'up', 'S', 'ground', 'back1d'),
    stairs('coalb', 'coalstore', 113, 6, 'up', 'S', 'ground', 'store1d'),
  ],
};
